import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, MenuItem, Box, Typography, FormControl, Select, TextField
} from '@mui/material';
import HelpIcon from './HelpIcon';
import { useChartOverlays } from '../context/ChartOverlayContext';
import { logActivity } from '../utils/logActivity';
import { buildDeclinationStudyReport } from '../utils/declinationStudyReport';
import { useAuth } from '../context/AuthContext';

const planets = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter',
  'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu'
];
const planetsWithoutSun = planets.filter(p => p !== 'Sun');
const COLORS = [
  'Red', 'Green', 'Blue', 'Yellow', 'Orange', 'Purple',
  'Black', 'White', 'Gray', 'Brown', 'Pink', 'Cyan'
];
const DECLINATION_OPTIONS = [
  'Parallel', 'Contra Parallel', 'Parallel North', 'Parallel South', 'Zero Declination'
];

// Color mapping for planet2
const PLANET2_COLOR_MAP = {
  'Sun': 'Orange',
  'Moon': 'Red',
  'Mercury': 'Gray',
  'Venus': 'Green',
  'Mars': 'Pink',
  'Jupiter': 'Blue',
  'Saturn': 'Brown',
  'Uranus': 'Cyan',
  'Neptune': 'Purple',
  'Pluto': 'Black',
  'Rahu': 'Teal',
  'Ketu': 'Yellow',
};

export default function DeclinationStudyDialog({ open, onClose, barData, onJournalAppend }) {
  const { addOverlay } = useChartOverlays();
  const { user, api } = useAuth();

  const [color, setColor] = useState(COLORS[0]);
  const frameOfReference = 'Geocentric'; // Read-only, always Geocentric
  const [planet1, setPlanet1] = useState('Sun');
  const [planet2, setPlanet2] = useState('Mercury');
  const [declination, setDeclination] = useState(DECLINATION_OPTIONS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-update color based on planet2
  useEffect(() => {
    if (PLANET2_COLOR_MAP[planet2]) {
      setColor(PLANET2_COLOR_MAP[planet2]);
    }
  }, [planet2]);

  const planetsAreIdentical = planet1 === planet2;

  // Build the study object for backend
  const buildStudyObject = () => {
    if (planetsAreIdentical) {
      alert("Planets cannot be identical for this study type.");
      return null;
    }
    // Map declination aspect to backend string
    const declinationAspectMap = {
      'Parallel': 'PARALLEL',
      'Contra Parallel': 'CONTRAPARALLEL',
      'Parallel North': 'NPARALLEL',
      'Parallel South': 'SPARALLEL',
    };

    // Use available barData for date range
    let fromDate = '';
    let toDate = '';
    // Try to get barData.dates from props or window/chart context if not present
    if (barData && Array.isArray(barData.dates) && barData.dates.length > 0) {
      fromDate = barData.dates[0];
      toDate = barData.dates[barData.dates.length - 1];
    } else if (window && window.chartData && Array.isArray(window.chartData.dates) && window.chartData.dates.length > 0) {
      fromDate = window.chartData.dates[0];
      toDate = window.chartData.dates[window.chartData.dates.length - 1];
      console.warn('[DeclinationStudyDialog] barData.dates missing, used window.chartData.dates as fallback:', window.chartData.dates);
    } else {
      console.warn('[DeclinationStudyDialog] No barData.dates or fallback chart data available! Dates will be blank.');
    }

    const studyObj = {
      type: 'declination',
      planet1,
      planet2,
      aspect: declinationAspectMap[declination] || declination,
      displayType: 'vLine',
      color,
      frameOfReference: frameOfReference || '',
      oldestBarDate: fromDate,
      newestBarDate: toDate,
      // Optionally, include symbol if needed by backend: symbol: barData?.symbol
    };
    console.log('[DeclinationStudyDialog] Study object to backend:', studyObj);
    return studyObj;
  };

  // Handle the Run button
  const handleRun = async () => {
    setError('');
    const studyObj = buildStudyObject();
    if (!studyObj) return;

    const eventDetails = {
      studyType: 'declination',
      parameters: {
        planet1: planet1,
        planet2: planet2,
        declination: declination,
      }
    };
    await logActivity(api, user, 'RUN_STUDY', eventDetails);

    setLoading(true);
    try {
      const response = await api.post('/api/declination/getDeclinationAspects', studyObj);

      const backendData = response.data;
      console.log('[DeclinationStudyDialog] Received response from backend:', backendData);

      // The commentary, logged to the Journal tab. Guarded separately from the overlay drawing
      // below: a report that cannot be built must not cost the customer their vertical lines.
      if (typeof onJournalAppend === 'function') {
        try {
          const eventDates = Array.isArray(backendData?.data)
            ? backendData.data.map(item => item.date)
            : [];
          const { report } = buildDeclinationStudyReport({
            planet1,
            planet2,
            aspect: studyObj.aspect,
            frame: frameOfReference || 'Geocentric',
            eventDates,
            barData,
            summary: backendData?.declinationSummary,
          });
          onJournalAppend({
            studyLabel: `${planet1} ${declination} ${planet2} (${frameOfReference || 'Geocentric'})`,
            reportText: report,
          });
        } catch (reportErr) {
          console.error('[DeclinationStudyDialog] Could not build the journal report:', reportErr);
        }
      }

      if (backendData && backendData.data && Array.isArray(backendData.data)) {
        backendData.data.forEach(item => {
          const newOverlay = {
            type: 'vLine',
            date: item.date,
            label: `${planet1} ${declination} ${planet2}\n${item.date.split('T')[0]}`,
            color: studyObj.color,
            planet1: studyObj.planet1,
            planet2: studyObj.planet2,
            aspect: studyObj.aspect,
            displayType: 'vLine',
          };
          console.log('[DeclinationStudyDialog] Adding vLine overlay:', newOverlay);
          addOverlay(newOverlay);
        });
      } else {
        console.warn('[DeclinationStudyDialog] Backend response is missing or has an invalid data array.');
      }

      onClose();
    } catch (err) {
      console.error('[DeclinationStudyDialog] Run failed:', err);
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const currentPlanetList = frameOfReference === 'Heliocentric' ? planetsWithoutSun : planets;

  const renderDeclinationFields = () => (
    <>
      <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%" gap={2}>
        <FormControl fullWidth size="small">
          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Planet 1</Typography>
          <Select value={planet1} onChange={(e) => setPlanet1(e.target.value)} error={planetsAreIdentical}>
            {currentPlanetList.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Planet 2</Typography>
          <Select value={planet2} onChange={(e) => setPlanet2(e.target.value)} error={planetsAreIdentical} disabled={declination === 'Zero Declination'}>
            {currentPlanetList.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Declination</Typography>
          <Select value={declination} onChange={(e) => setDeclination(e.target.value)}>
            {DECLINATION_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ style: { width: '560px', borderRadius: '12px' } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem', borderBottom: '1px solid #eee', pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Declination Study</span>
        <HelpIcon helpKey="declinationStudyDialog" />
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box display="flex" flexDirection="column" gap={2.5} width="100%">
          {renderDeclinationFields()}
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
            <Box display="flex" flexDirection="column" width="50%">
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Display type</Typography>
              <TextField value="Vertical Line" size="small" InputProps={{ readOnly: true }} />
            </Box>
            <FormControl fullWidth size="small" style={{ width: '50%' }}>
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Color</Typography>
              <Select value={color} onChange={(e) => setColor(e.target.value)} renderValue={(selected) => (
                <Box display="flex" alignItems="center">
                  <span style={{
                    display: 'inline-block',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: selected.toLowerCase(),
                    marginRight: 8,
                    border: '1px solid #bbb'
                  }} />
                  {selected}
                </Box>
              )}>
                {COLORS.map((c) => (
                  <MenuItem key={c} value={c}>
                    <Box display="flex" alignItems="center">
                      <span style={{
                        display: 'inline-block',
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        backgroundColor: c.toLowerCase(),
                        marginRight: 8,
                        border: '1px solid #bbb'
                      }} />
                      {c}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ color: 'orange', fontWeight: 600, fontSize: '1rem' }}>
              WARNING: Any declination study involving Moon takes more than a minute
            </Typography>
          </Box>
          {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px', borderTop: '1px solid #eee', justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="contained" sx={{ backgroundColor: 'grey.200', color: 'grey.800', '&:hover': { backgroundColor: 'grey.300' } }}>
          Cancel
        </Button>
        <Button onClick={handleRun} variant="contained" disabled={planetsAreIdentical || loading}>
          {loading ? 'Running...' : 'Run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DeclinationStudyDialog.propTypes = {
  /** Appends the run's declination commentary to the Message Panel's Journal tab. */
  onJournalAppend: PropTypes.func,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  barData: PropTypes.object // Should include .dates[] and optionally .symbol
};